import PropTypes from 'prop-types';
import {useState, useEffect, useRef} from 'react';
import {flattenConnection, useProductOptions} from '@shopify/hydrogen';
import {Helmet} from 'react-helmet';
import {useImageOptimizer} from '~/hooks/useImageOptimizer';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Navigation, Mousewheel} from 'swiper/modules';

/**
 * ImageSelectorOption component
 * Displays a single image option for the product image selector
 * Uses the useImageOptimizer hook to optimize the image URL
 */

function ImageSelectorOption({image, active, onClick, index, carouselActive}) {
  const optimizedImage = useImageOptimizer(image?.asset?.url, 150);

  return (
    <button
      className={`image-selector__option ${active ? 'active' : ''} ${
        carouselActive ? 'carousel-active' : ''
      }`}
      onClick={() => onClick(image)}
    >
      <img
        src={optimizedImage?.optimizedImageSrc}
        alt={image?.store?.title ?? `Variant Image ${index + 1}`}
        loading="lazy"
      />
    </button>
  );
}

/**
 * ProductImageSelector component
 * Displays images of a particular product variant with selections that toggle the main image
 * Uses context from ProductOptionsProvider parent component to track selected variant
 */

export function ProductImageSelector({sanityProduct}) {
  // The useProductOptions hook returns an object that enables you to keep track of the selected variant
  const {selectedVariant: selectedShopifyVariant} = useProductOptions();
  const [zoomPosition, setZoomPosition] = useState({x: 0, y: 0});
  const [isZoomVisible, setIsZoomVisible] = useState(false);
  const imageRef = useRef(null);
  const enableZoom = sanityProduct?.enableZoom;

  const swiperRef = useRef(null);

  const updateNavigationVisibility = (swiper) => {
    const prevButtons = document.querySelectorAll(
      '.swiper-button-prev.custom-swiper-button',
    );
    const isRealFirst = swiper.realIndex === 0;

    prevButtons.forEach((prevButton) => {
      if (isRealFirst) {
        swiper.allowSlidePrev = false;
        prevButton.style.visibility = 'hidden';
      } else {
        swiper.allowSlidePrev = true;
        prevButton.style.visibility = 'visible';
      }
    });
  };

  const selectedSanityVariant = sanityProduct?.store?.variants.filter(
    (variant) => {
      const variantSku = variant?.store?.sku ?? 'a';
      const selectedVariantSku = selectedShopifyVariant?.sku ?? 'b';
      return variantSku === selectedVariantSku;
    },
  );

  const primaryImages = sanityProduct?.images ?? {};
  const {images} = selectedSanityVariant?.[0] ?? {};
  const [activeMedia, setActiveMedia] = useState();
  const [pageTitle, setPageTitle] = useState('');
  const [pageDescription, setPageDescription] = useState('');
  const previousVariantRef = useRef();

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  }, [images]);

  // when the selected variant changes, reset the active media to the first image
  useEffect(() => {
    if (previousVariantRef.current !== selectedSanityVariant?.[0]?.store?.sku) {
      setActiveMedia(images?.[0]);
      previousVariantRef.current = selectedSanityVariant?.[0]?.store?.sku;
    }
    // Extract title and description from the selected variant
    const title =
      selectedSanityVariant[0]?.seo?.title ??
      sanityProduct?.store?.title ??
      'Default Product Title';
    const description =
      selectedSanityVariant[0]?.seo?.description ??
      sanityProduct?.store?.description ??
      'Default Product Description';

    // Update state
    setPageTitle(title);
    setPageDescription(description);
  }, [selectedSanityVariant, sanityProduct]);

  const activeMediaImage = useImageOptimizer(
    activeMedia?.asset?.url ?? selectedShopifyVariant?.image?.url,
    800,
  );

  const handleMouseMove = (e) => {
    if (!enableZoom || !imageRef.current) return;
    const {left, top, width, height} = imageRef.current.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - top) / height) * 100;
    setZoomPosition({x, y});
  };
  const handleMouseEnter = () => enableZoom && setIsZoomVisible(true);
  const handleMouseLeave = () => enableZoom && setIsZoomVisible(false);

  const useCarousel = sanityProduct?.carouselOnAltImages;

  const onSwiper = (swiper) => {
    swiperRef.current = swiper;
    updateNavigationVisibility(swiper);
  };

  const updateIndex = (swiper) => {
    Promise.resolve().then(() => {
      updateNavigationVisibility(swiper);
    });
  };
  return (
    <div className="image-selector">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div
        className="image-selector__active"
        ref={imageRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          src={activeMediaImage.optimizedImageSrc}
          alt={activeMedia?.store?.title ?? 'Selected Product Variant'}
          loading="lazy"
        />
        {isZoomVisible && (
          <div
            className="zoom-lens"
            style={{
              background: `url('${activeMediaImage.optimizedImageSrc}') no-repeat center/200%, #fff`,
              backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
              backgroundSize: '150%',
            }}
          ></div>
        )}
      </div>
      {useCarousel ? (
        <Swiper
          slidesPerView={3}
          loop={true}
          breakpoints={{
            1024: {
              slidesPerView: 4,
            },
          }}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          onInit={onSwiper}
          onSlideChange={updateIndex}
          mousewheel={{
            sensitivity: 1,
            releaseOnEdges: false,
          }}
          modules={[Navigation, Mousewheel]}
        >
          {images &&
            images.map((image, index) => (
              <SwiperSlide key={`${image._key}-${index}`}>
                <ImageSelectorOption
                  image={image}
                  active={image === activeMedia}
                  onClick={(image) => setActiveMedia(image)}
                  index={index}
                  carouselActive={useCarousel}
                />
              </SwiperSlide>
            ))}
          {Array.isArray(primaryImages) &&
            primaryImages.map((image, index) => (
              <SwiperSlide key={`${image._key}-${index}`}>
                <ImageSelectorOption
                  image={image}
                  active={image === activeMedia}
                  onClick={(image) => setActiveMedia(image)}
                  index={index}
                  carouselActive={useCarousel}
                />
              </SwiperSlide>
            ))}

          <div className="swiper-button-prev custom-swiper-button"></div>
          <div className="swiper-button-next custom-swiper-button"></div>
        </Swiper>
      ) : (
        <div className="image-selector__options">
          {images &&
            images.map((image, index) => (
              <ImageSelectorOption
                key={image._key}
                image={image}
                active={image === activeMedia}
                onClick={(image) => setActiveMedia(image)}
                index={index}
                carouselActive={useCarousel}
              />
            ))}
          {Array.isArray(primaryImages) &&
            primaryImages.map((image, index) => (
              <ImageSelectorOption
                key={image._key}
                image={image}
                active={image === activeMedia}
                onClick={(image) => setActiveMedia(image)}
                index={index}
                carouselActive={useCarousel}
              />
            ))}
        </div>
      )}
    </div>
  );
}

ProductImageSelector.propTypes = {
  sanityProduct: PropTypes.object.isRequired,
};
